import React from 'react';
 import Icon from'@/components/ui/AppIcon';

interface Feature {
  id: string
  iconName: string
  title: string
  description: string
}

const WhyChooseSection: React.FC = () => {
  const features: Feature[] = [
    {
      id: 'feature-1',
      iconName: 'BuildingOffice2Icon',
      title: 'Direct from Source',
      description:
        'No middlemen. We own mining operations in North Kivu, ensuring the best prices and quality control.',
    },
    {
      id: 'feature-2',
      iconName: 'ChartBarSquareIcon',
      title: 'Transparent Pricing',
      description:
        'Live market prices updated every 15 minutes. No hidden fees. What you see is what you pay.',
    },
    {
      id: 'feature-3',
      iconName: 'TruckIcon',
      title: 'Fast Shipping',
      description:
        'International shipping in 3-7 days via secure courier. Full insurance and tracking included.',
    },
    {
      id: 'feature-4',
      iconName: 'BeakerIcon',
      title: 'Quality Guaranteed',
      description:
        '99.9% purity 24K gold. Lab-tested certificates included with every shipment for your peace of mind.',
    },
    {
      id: 'feature-5',
      iconName: 'ChatBubbleLeftRightIcon',
      title: 'Easy Communication',
      description:
        'WhatsApp support in English, French, Swahili. Instant responses during business hours.',
    },
    {
      id: 'feature-6',
      iconName: 'ShieldCheckIcon',
      title: 'Compliance First',
      description:
        'Full Kimberley Process compliance. Conflict-free guarantee. All certifications available upon request.',
    },
  ]

  return (
    <section className="py-24 md:py-32 px-6 md:px-12 bg-card border-t border-border">
      <div className="w-full max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-8 reveal">
          <div>
            <span className="text-xs font-body font-bold uppercase tracking-[0.4em] text-muted-foreground mb-4 block">
              Why Choose Us
            </span>
            <h2 className="text-5xl md:text-6xl font-display font-bold tracking-tight text-foreground">
              What Makes Us <br />
              <span className="text-muted-foreground">Different.</span>
            </h2>
          </div>
          <p className="text-base font-body text-muted-foreground max-w-md leading-relaxed">
            From direct sourcing to international compliance, we prioritize transparency and quality
            in every transaction.
          </p>
        </div>

        {/* Feature Grid with Stagger Animation */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 stagger-children">
          {features.map((feature, index) => (
            <div
              key={feature.id}
              className="group glass-card p-8 rounded-3xl border border-border shadow-lg hover:shadow-2xl transition-smooth hover:-translate-y-3 hover:scale-105 cursor-pointer relative overflow-hidden reveal"
              style={{ animationDelay: `${0.1 + index * 0.1}s` }}
            >
              {/* Shimmer Effect */}
              <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-primary/10 to-transparent pointer-events-none group-hover:animate-shimmer-effect"></div>

              <div className="relative z-10">
                {/* Icon with Enhanced Hover */}
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary transition-smooth group-hover:scale-110 group-hover:rotate-6">
                  <Icon
                    name={feature.iconName as any}
                    size={28}
                    className="text-primary group-hover:text-primary-foreground transition-smooth group-hover:scale-110"
                  />
                </div>

                {/* Content */}
                <h3 className="text-xl font-display font-bold text-foreground mb-3 group-hover:text-primary transition-smooth">
                  {feature.title}
                </h3>
                <p className="text-sm font-body text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>

                {/* Gold Border Accent */}
                <div className="absolute bottom-0 left-0 w-full h-1 bg-primary opacity-0 group-hover:opacity-100 transition-smooth rounded-b-3xl"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default WhyChooseSection